/**
 * Constants and drum mappings for Songsterr Parser
 */

import type { LaneIndex } from "./types";

/**
 * Lane names for 8-lane drum setup
 * 0: Hi-Hat, 1: Crash, 2: Ride, 3: Snare, 4: Tom 1, 5: Tom 2, 6: Floor Tom, 7: Kick
 */
export const LANE_NAMES = [
  "Hi-Hat",
  "Crash",
  "Ride",
  "Snare",
  "Tom 1",
  "Tom 2",
  "Floor Tom",
  "Kick",
] as const;

/**
 * GM/GS MIDI pitch to lane mapping
 * Based on Songsterr fret values
 */
export const GM_TO_LANE: Record<number, LaneIndex> = {
  // Kick (Lane 7)
  35: 7,
  36: 7,

  // Snare (Lane 3)
  31: 3,
  38: 3,
  39: 3, // Songsterr snare ghost note
  40: 3,

  // Hi-Hat (Lane 0)
  42: 0,
  44: 0,
  46: 0,
  82: 0, // Songsterr hi-hat (common)
  80: 0,
  81: 0,
  83: 0,
  84: 0,
  85: 0,

  // Crash (Lane 1)
  49: 1,
  52: 1,
  55: 1,
  57: 1,
  97: 1, // Songsterr crash
  69: 1, // Songsterr crash/china

  // Ride (Lane 2)
  51: 2,
  92: 2, // Songsterr ride
  98: 2, // Songsterr ride

  // Tom 1 (Lane 4)
  50: 4,

  // Tom 2 (Lane 5)
  48: 5,

  // Floor Tom (Lane 6)
  41: 6,
  43: 6,
  45: 6,
  47: 6,
};

/**
 * Heuristic function to determine lane from pitch
 * Fallback for unmapped fret values
 */
export function laneFromPitchHeuristic(pitch: number): LaneIndex {
  // Kick
  if (pitch === 35 || pitch === 36) return 7;
  
  // Snare
  if (pitch === 31 || pitch === 38 || pitch === 39 || pitch === 40) return 3;
  
  // Hi-Hat
  if (pitch === 42 || pitch === 44 || pitch === 46) return 0;
  if (pitch >= 80 && pitch <= 85) return 0; // Songsterr hi-hat range
  
  // Crash
  if (pitch === 49 || pitch === 52 || pitch === 55 || pitch === 57 || pitch === 97 || pitch === 69) return 1;
  
  // Ride
  if (pitch === 51 || pitch === 92 || pitch === 98) return 2;
  
  // Tom 1
  if (pitch === 50) return 4;
  
  // Tom 2
  if (pitch === 48) return 5;
  
  // Floor Tom
  if (pitch === 41 || pitch === 43 || pitch === 45 || pitch === 47) return 6;

  // Fallback - try to guess based on range
  if (pitch < 40) return 7; // Low = Kick
  if (pitch >= 80 && pitch < 90) return 0; // 80s = Hi-Hat
  if (pitch >= 90 && pitch < 97) return 2; // 90s = Ride/Toms
  
  return 3; // Default to snare
}

/**
 * Convert lane index to bit mask
 */
export function maskBitForLane(lane: LaneIndex): number {
  return 1 << lane;
}

/**
 * Extract lanes from bit mask
 */
export function lanesFromMask(mask: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < 8; i++) {
    if ((mask & (1 << i)) !== 0) {
      out.push(i);
    }
  }
  return out;
}

/**
 * Clamp value between min and max
 */
export function clamp(v: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, v));
}
